import type { RefObject } from 'react'
import type { AggregatedOrderView } from '@/types/api/aggregated/order'
import type {
  SystemUpdateEventMessage,
  TradingEventMessage,
} from '@/types/api/events/trading-event'

import { cloneDeep } from 'lodash'

import { createLogger } from '@/lib/logger'

import { updateLatestMsgNumber, upsertOrder } from './helpers'

const LOGGER = createLogger('utils/trade-blotter/trading-events/system-update')

function constructUpdatedOrderView(
  systemUpdateEventMessage: SystemUpdateEventMessage,
  existingOrderView: AggregatedOrderView | null,
): AggregatedOrderView {
  const existingTradingEvents = existingOrderView?.trading_events || []
  // Want to prevent duplicate trading events from appearing in the blotter
  // in the event we receive a duplicate message because we reconnected to
  // the websocket at a high water mark we have already processed.
  const filteredTradingEvents = existingTradingEvents.filter(
    (event: TradingEventMessage) =>
      event.msg_number !== systemUpdateEventMessage.msg_number,
  )
  return updateLatestMsgNumber(
    {
      ...(existingOrderView || {}),
      id: existingOrderView?.id || systemUpdateEventMessage.event.order_id,
      allocation_status: existingOrderView?.allocation_status || null,
      account_requests: existingOrderView?.account_requests || null,
      allocation_info: existingOrderView?.allocation_info || null,
      // TODO: We will need to refactor this when we can do multi-custodial trades
      custodian_id: existingOrderView?.custodian_id || null,
      last_allocation_update_event:
        existingOrderView?.last_allocation_update_event || null,
      last_order_update_event:
        existingOrderView?.last_order_update_event || null,
      last_system_update_event: systemUpdateEventMessage.event,
      // Cloning since we are concatenating to the array and AG Grid needs a new reference to detect changes
      trading_events: cloneDeep(
        filteredTradingEvents.concat(systemUpdateEventMessage),
      ),
      rfq_views: existingOrderView?.rfq_views || null,
      reject_reason: existingOrderView?.reject_reason || null,
      cancel_reason: existingOrderView?.cancel_reason || null,
    },
    systemUpdateEventMessage.msg_number,
  )
}

/**
 * Processes system updates received via HTTP and updates the ordersMap.
 * Only called as part of the transformResponse callback in the TradingSlice.
 * @param AggregatedOrderViewsMap Map of existing AggregatedOrderView objects
 * @param tradingEvent Incoming trading event
 */
export const processSystemUpdate = (
  AggregatedOrderViewsMap: Map<string, AggregatedOrderView>,
  tradingEvent: TradingEventMessage,
): void => {
  const systemUpdateEventMessage = tradingEvent as SystemUpdateEventMessage
  const orderId = systemUpdateEventMessage.event.order_id
  if (!orderId) {
    LOGGER.warn(
      'Received system update event via HTTP response with no order_id',
      { event: tradingEvent },
    )
    return
  }
  const existingOrderView = AggregatedOrderViewsMap.get(orderId) || null
  const updatedOrderView = constructUpdatedOrderView(
    systemUpdateEventMessage,
    existingOrderView,
  )
  AggregatedOrderViewsMap.set(orderId, updatedOrderView)
}

/**
 * Handles system updates received via WebSocket and updates the data cached in Redux.
 * @param tradingEvent Incoming trading event
 * @param updateCachedData Function to update the cached data in Redux
 */
export const handleSystemUpdate = (
  tradingEvent: TradingEventMessage,
  aggregatedOrderViewsRef: RefObject<Map<string, AggregatedOrderView>>,
  onUpdateApplied: ({
    toAdd,
    toUpdate,
    toRemove,
  }: {
    toAdd?: AggregatedOrderView[]
    toUpdate?: AggregatedOrderView[]
    toRemove?: AggregatedOrderView[]
  }) => void,
) => {
  const systemUpdateEventMessage = tradingEvent as SystemUpdateEventMessage
  const orderId = systemUpdateEventMessage.event.order_id
  if (!orderId) {
    LOGGER.warn('Received system update event via WebSocket with no order_id', {
      event: tradingEvent,
    })
    return
  }

  const map = aggregatedOrderViewsRef.current
  const existingOrderView = map.get(orderId) || null
  const updatedOrderView = constructUpdatedOrderView(
    systemUpdateEventMessage,
    existingOrderView,
  )
  upsertOrder(map, orderId, updatedOrderView, onUpdateApplied)
}
